import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Offcanvas, Button } from 'react-bootstrap';
import { BsPencil, BsX } from 'react-icons/bs';
import {
    FaFileContract,
    FaCalendarAlt,
    FaHistory,
    FaShieldAlt,
    FaUsers,
    FaRegCalendarCheck,
    FaInfoCircle,
    FaRocket,
    FaChevronDown,
    FaChevronUp,
    FaUserCheck,
    FaCommentDots
} from "react-icons/fa";

const LicenseInfoDetails = ({ show, onHide, licenseId, onEditLicense }) => {
    const [licenseDetails, setLicenseDetails] = useState(null);
    const [activeTab, setActiveTab] = useState('licenseDetails');
    const [extensionDetails, setExtensionDetails] = useState([]);
    const [localShow, setLocalShow] = useState(show);
    const [expandedGroups, setExpandedGroups] = useState({ 'Roles & Users': true, 'Modules & Features': true });

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    useEffect(() => { setLocalShow(show); }, [show]);

    useEffect(() => {
        if (localShow && licenseId) {
            setLicenseDetails(null);
            setActiveTab('licenseDetails');
            fetchLicenseDetails();
        }
    }, [localShow, licenseId]);

    const fetchLicenseDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(`${backendUrl}/api/v360-license-info/${licenseId}`);
            
            // Adjust depending on how the backend returns the data
            const data = response.data.data || response.data; 
            setLicenseDetails(data);
        } catch (error) {
            console.error('Error fetching license details:', error);
        }
    };

    const fetchExtensionDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const response = await axios.get(`${backendUrl}/api/v360-license-info/${licenseId}/extensions`);
            const data = response.data.data || response.data;
            setExtensionDetails(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching extension details:', error);
            if (licenseDetails && licenseDetails.extension_type) {
                setExtensionDetails([{
                    id: 1,
                    created_at: licenseDetails.updated_at || new Date().toISOString(),
                    extension_days: licenseDetails.extension_days,
                    extension_type: licenseDetails.extension_type,
                    new_validity_date: licenseDetails.extended_validity || licenseDetails.validity
                }]);
            } else {
                setExtensionDetails([]);
            }
        }
    };

    const handleClose = () => {
        setLicenseDetails(null);
        setActiveTab('licenseDetails');
        onHide();
    };

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const datePart = String(dateStr).split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
    };

    const getStatus = (license) => {
        if (!license) return 'Active';
        const validityStr = license.extended_validity || license.validity || '';
        if (!validityStr) return 'Active';
        
        const datePart = String(validityStr).split('T')[0];
        const parts = datePart.split('-');
        let y, m, d;
        if (parts.length === 3) {
            if (parts[0].length === 4) {
                [y, m, d] = parts.map(Number);
            } else {
                d = Number(parts[0]);
                m = Number(parts[1]);
                y = Number(parts[2]);
            }
        }
        if (!y || isNaN(m) || isNaN(d)) return 'Active';
        const expiryDate = new Date(y, m - 1, d);
        
        if (expiryDate < new Date().setHours(0,0,0,0)) return 'Expired';
        if (license.extended_validity) return 'Extended';
        return 'Active';
    };

    const rolesFields = [
        { key: 'user_login', label: 'User Login' },
        { key: 'user_create', label: 'User Create' },
        { key: 'role_s_admin', label: 'Super Admin' },
        { key: 'role_admin', label: 'Admin' },
        { key: 'role_supervisor', label: 'Supervisor' },
        { key: 'role_teamlead', label: 'Teamlead' },
        { key: 'role_analyst', label: 'Analyst' },
        { key: 'role_cagent', label: 'C. Agent' },
        { key: 'role_vagent', label: 'V. Agent' },
        { key: 'role_tagent', label: 'T. Agent' },
        { key: 'role_agent', label: 'Agent' },
        { key: 'role_agent_plus', label: 'Agent Plus' }
    ];

    const modulesFields = [
        { key: 'module_email', label: 'Email' },
        { key: 'module_voicemail', label: 'Voicemail' },
        { key: 'module_chat', label: 'Chat' },
        { key: 'module_interaction', label: 'Interaction' },
        { key: 'module_voice', label: 'Voice' },
        { key: 'module_video', label: 'Video' },
        { key: 'module_api', label: 'API' },
        { key: 'mobile_app', label: 'Mobile App' },
        { key: 'wallboard', label: 'Wallboard' },
        { key: 'gen_ai', label: 'Gen AI' },
        { key: 'toolbar_app', label: 'Toolbar App' },
        { key: 'ai_assist', label: 'AI Assist' },
        { key: 'ai_assist_pro', label: 'AI Pro' }
    ];

    const dynamicBreakdown = [
        { name: 'Roles & Users', fields: rolesFields, icon: <FaUsers className="text-primary" />, color: '#6366f1' },
        { name: 'Modules & Features', fields: modulesFields, icon: <FaRocket className="text-warning" />, color: '#f59e0b' }
    ];

    return (
        <Offcanvas
            show={localShow}
            onHide={handleClose}
            placement="end"
            style={{ width: '1150px', zIndex: 1055 }}
            className="License-offcanvas"
        >
            <Offcanvas.Header className="border-bottom bg-white py-3">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center">
                        <div className="bg-primary rounded-circle p-2 me-3 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                            <FaFileContract className="text-white" size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bolder text-dark" style={{ letterSpacing: '-0.3px' }}>
                                License Details - Tenant: {licenseDetails?.tenant_id || 'N/A'}
                            </h5>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <span className={`badge font-size-10 fw-bold px-2 ${getStatus(licenseDetails) === 'Expired' ? 'bg-soft-danger text-danger' : 'bg-soft-success text-success'}`}>
                                    {getStatus(licenseDetails).toUpperCase()}
                                </span>
                                {licenseDetails?.acc_status && (
                                    <span className="badge bg-soft-info text-info font-size-10 fw-bold px-2">
                                        Status: {String(licenseDetails.acc_status).toLowerCase()}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            variant="primary"
                            size="sm"
                            className="rounded-pill px-3 shadow-sm border-0 d-flex align-items-center gap-2"
                            onClick={() => {
                                handleClose();
                                if (onEditLicense && licenseDetails) {
                                    onEditLicense(licenseDetails);
                                }
                            }}
                            style={{ backgroundColor: '#6366f1' }}
                        >
                            <BsPencil size={14} /> <span className="fw-bold font-size-12">Edit</span>
                        </Button>
                        <Button variant="light" size="sm" onClick={handleClose} className="rounded-circle border-0 p-2">
                            <BsX size={22} className="text-muted" />
                        </Button>
                    </div>
                </div>
            </Offcanvas.Header>

            <Offcanvas.Body className="p-0 bg-light bg-opacity-10">
                <div className="border-bottom bg-white shadow-sm sticky-top" style={{ zIndex: 10 }}>
                    <ul className="nav nav-tabs nav-tabs-custom nav-justified border-0" role="tablist">
                        <li className="nav-item">
                            <button
                                className={`nav-link border-0 py-3 fw-bolder font-size-12 text-uppercase ls-1 ${activeTab === 'licenseDetails' ? 'active' : 'text-muted'}`}
                                onClick={() => setActiveTab('licenseDetails')}
                            >
                                <FaInfoCircle className="me-2" size={14} />
                                License Configuration
                            </button>
                        </li>
                        <li className="nav-item">
                            <button
                                className={`nav-link border-0 py-3 fw-bolder font-size-12 text-uppercase ls-1 ${activeTab === 'extensionInfo' ? 'active' : 'text-muted'}`}
                                onClick={() => { setActiveTab('extensionInfo'); fetchExtensionDetails(); }}
                            >
                                <FaHistory className="me-2" size={14} />
                                Extension Archive
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="p-4" style={{ minHeight: '500px' }}>
                    {!licenseDetails ? (
                        <div className="text-center py-5">
                            <div className="spinner-grow text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
                            <p className="mt-3 text-muted fw-bold font-size-13 text-uppercase ls-1">Retrieving Records...</p>
                        </div>
                    ) : activeTab === 'licenseDetails' ? (
                        <>
                            {/* Quick Summary Overview Cards */}
                            <div className="row g-3 mb-4">
                                {[
                                    { label: 'Validity Date', val: formatDisplayDate(licenseDetails?.extended_validity || licenseDetails?.validity), icon: FaRegCalendarCheck, color: '#6366f1', bg: 'rgba(99, 102, 241, 0.08)', col: 3 },
                                    // { label: 'Exp. Status', val: getStatus(licenseDetails), icon: FaShieldAlt, color: getStatus(licenseDetails) === 'Expired' ? '#ef4444' : '#10b981', bg: getStatus(licenseDetails) === 'Expired' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.08)', col: 2 },
                                    { label: 'Acc. Status', val: licenseDetails?.acc_status ? String(licenseDetails.acc_status).toLowerCase() : 'N/A', icon: FaUserCheck, color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.08)', col: 2 },
                                    { label: 'Reason', val: licenseDetails?.status_reason || '-', icon: FaCommentDots, color: '#8b5cf6', bg: 'rgba(139, 92, 246, 0.08)', col: 5 }
                                ].map((kpi, i) => (
                                    <div className={`col-md-${kpi.col}`} key={i}>
                                        <div className="bg-white p-3 rounded-4 shadow-sm border border-faint h-100" title={kpi.val}>
                                            <div className="d-flex align-items-center mb-1">
                                                <div className="rounded-circle p-2 me-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: kpi.bg, color: kpi.color, width: '32px', height: '32px', flexShrink: 0 }}>
                                                    <kpi.icon size={14} />
                                                </div>
                                                <span className="text-muted text-uppercase fw-bold font-size-10 ls-1 text-truncate">{kpi.label}</span>
                                            </div>
                                            <h5 className="mb-0 fw-bolder text-dark px-1 text-truncate" style={{ fontSize: '14px' }}>{kpi.val || '0'}</h5>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="mb-5">
                                {dynamicBreakdown.map((cat) => {
                                    const catFields = cat.fields.filter(f => Number(licenseDetails[f.key]) > 0);
                                    if (catFields.length === 0) return null;
                                    
                                    const isExpanded = expandedGroups[cat.name] !== undefined ? expandedGroups[cat.name] : true;
                                    const isModuleCat = cat.name.includes('Modules');

                                    return (
                                        <div key={cat.name} className="card border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                                            <div 
                                                className="card-header border-0 d-flex justify-content-between align-items-center p-3" 
                                                onClick={() => toggleGroup(cat.name)}
                                                style={{ cursor: 'pointer', backgroundColor: isModuleCat ? 'rgba(16, 185, 129, 0.04)' : 'rgba(99, 102, 241, 0.04)' }}
                                            >
                                                <div className="d-flex align-items-center mb-0">
                                                    <div className="bg-white rounded p-2 me-3 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                                        {cat.icon}
                                                    </div>
                                                    <h6 className={`mb-0 fw-bolder text-uppercase ${isModuleCat ? 'text-success' : 'text-primary'}`} style={{ fontSize: '12px', letterSpacing: '1px' }}>{cat.name}</h6>
                                                </div>
                                                <div className="bg-white rounded-circle p-1 shadow-xs mt-1">
                                                    {isExpanded ? <FaChevronUp size={12} className={isModuleCat ? 'text-success' : 'text-primary'} /> : <FaChevronDown size={12} className={isModuleCat ? 'text-success' : 'text-primary'} />}
                                                </div>
                                            </div>
                                            {isExpanded && (
                                                <div className="card-body p-4 bg-light bg-opacity-50 border-top">
                                                    <div className="row g-4">
                                                        {catFields.map((field, idx) => (
                                                            <div key={idx} className="col-md-3 col-sm-6">
                                                                <div 
                                                                    className="bg-white border rounded-4 p-3 h-100 d-flex align-items-center justify-content-between shadow-sm transition-all"
                                                                >
                                                                    <div>
                                                                        <span className="text-muted text-uppercase fw-bold d-block mb-1" style={{ fontSize: '10px', letterSpacing: '0.5px' }}>
                                                                            {field.label}
                                                                        </span>
                                                                        <h4 className={`mb-0 fw-bolder ${isModuleCat ? 'text-success' : 'text-primary'}`} style={{ letterSpacing: '-0.5px' }}>
                                                                            {Number(licenseDetails[field.key])}
                                                                        </h4>
                                                                    </div>
                                                                    <div 
                                                                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" 
                                                                        style={{ 
                                                                            backgroundColor: isModuleCat ? 'rgba(16, 185, 129, 0.1)' : 'rgba(99, 102, 241, 0.1)', 
                                                                            color: isModuleCat ? '#10b981' : '#6366f1', 
                                                                            width: '40px', height: '40px' 
                                                                        }}
                                                                    >
                                                                        {cat.icon}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="card border-0 shadow-sm rounded-4 mb-5">
                                <div className="card-body p-4">
                                    <div className="d-flex align-items-center mb-4">
                                        <div className="bg-soft-primary rounded p-2 me-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(85, 110, 230, 0.08)', width: '36px', height: '36px' }}>
                                            <FaInfoCircle className="text-primary" size={16} />
                                        </div>
                                        <h6 className="mb-0 fw-bolder text-uppercase ls-1 font-size-12 text-dark">Configuration Details & Remarks</h6>
                                    </div>
                                    <div className="row g-4">
                                        <div className="col-md-4">
                                            <div className="p-3 bg-light bg-opacity-25 rounded-3 border h-100">
                                                <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-50"><FaCalendarAlt className="me-2" size={10} /> Start Date</label>
                                                <h6 className="mb-0 fw-bold text-dark font-size-13">{formatDisplayDate(licenseDetails?.start_date)}</h6>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 bg-light bg-opacity-25 rounded-3 border h-100">
                                                <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-50"><FaRegCalendarCheck className="me-2" size={10} /> Current Validity</label>
                                                <h6 className="mb-0 fw-bold text-dark font-size-13">{formatDisplayDate(licenseDetails?.extended_validity || licenseDetails?.validity)}</h6>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 bg-light bg-opacity-25 rounded-3 border h-100">
                                                <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-50"><FaRegCalendarCheck className="me-2" size={10} /> IP Check</label>
                                                <h6 className="mb-0 fw-bold text-dark font-size-13">{licenseDetails?.ip_check ? 'Enabled' : 'Disabled'}</h6>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="p-3 bg-light bg-opacity-25 rounded-3 border h-100">
                                                <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-50"><FaInfoCircle className="me-2" size={10} /> Remark</label>
                                                <h6 className="mb-0 fw-bold text-dark font-size-13" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{licenseDetails?.remark || 'N/A'}</h6>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="p-3 bg-light bg-opacity-25 rounded-3 border h-100">
                                                <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-50"><FaInfoCircle className="me-2" size={10} /> Additional Remarks</label>
                                                <h6 className="mb-0 fw-bold text-dark font-size-13" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{licenseDetails?.remarks || 'N/A'}</h6>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                            <div className="p-3 bg-soft-primary bg-opacity-10 border-bottom">
                            </div>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="bg-light bg-opacity-50">
                                        <tr>
                                            <th className="ps-4 font-size-11 text-uppercase text-muted fw-bold py-3">#</th>
                                            <th className="font-size-11 text-uppercase text-muted fw-bold py-3">Execution Date</th>
                                            <th className="font-size-11 text-uppercase text-muted fw-bold py-3">Extended By</th>
                                            <th className="text-center font-size-11 text-uppercase text-muted fw-bold py-3">Archived Validity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {extensionDetails.length === 0 ? (
                                            <tr><td colSpan={4} className="text-center text-muted py-5 font-size-13 italic">No extension history available for this record.</td></tr>
                                        ) : extensionDetails.map((ext, i) => (
                                            <tr key={ext.id}>
                                                <td className="ps-4 text-muted font-size-12 fw-bold">{i + 1}</td>
                                                <td>
                                                    <div className="text-dark fw-bold font-size-13">{ext.created_at ? new Date(ext.created_at).toLocaleDateString() : 'N/A'}</div>
                                                    <small className="text-muted font-size-10">{ext.created_at ? new Date(ext.created_at).toLocaleTimeString() : ''}</small>
                                                </td>
                                                <td>
                                                    <span className="badge rounded-pill bg-soft-success text-success px-3 py-2 font-size-11 fw-bold">
                                                        +{ext.extension_days} {ext.extension_type}
                                                    </span>
                                                </td>
                                                <td className="text-center">
                                                    <div className="text-primary fw-black font-size-14 text-nowrap">{formatDisplayDate(ext.new_validity_date)}</div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-light bg-opacity-20">
                                        <tr>
                                           
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default LicenseInfoDetails;
